import {
  type APIAttachment,
  type APIEmbed,
  type APIMessage,
  type APIStickerItem,
  formatFileSize,
  formatMessageTime,
  roleColorHex,
  stickerUrl,
} from '@alternativamais/chat-core';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { abrirCamada } from '../../../chat/camadas';
import { useEstado } from '../../../chat/contexto';
import { useAjusteDaConta } from '../../../chat/ganchos';
import { usePreferencias } from '../../../chat/preferencias';
import { Icone } from '../../../componentes/Icone';
import { Markdown } from '../../../componentes/Markdown';
import { ARQUIVOS } from '../../../config';
import { noTamanho } from '../../../componentes/imagens';

/** Mídia cabe em 400×300 (como o Discord, guardando a proporção). */
export function caber(
  largura: number | undefined,
  altura: number | undefined,
  max = { largura: 400, altura: 300 },
): { width: number; height: number } {
  if (!largura || !altura) return { width: max.largura, height: max.altura };
  const escala = Math.min(1, max.largura / largura, max.altura / altura);
  return {
    width: Math.max(1, Math.round(largura * escala)),
    height: Math.max(1, Math.round(altura * escala)),
  };
}

function tipoDe(
  anexo: APIAttachment,
): 'imagem' | 'video' | 'audio' | 'arquivo' {
  const tipo = anexo.content_type ?? '';
  const nome = anexo.filename.toLowerCase();
  if (tipo.startsWith('image/') || /\.(png|jpe?g|gif|webp|avif)$/.test(nome)) {
    return 'imagem';
  }
  if (tipo.startsWith('video/') || /\.(mp4|webm|mov)$/.test(nome))
    return 'video';
  if (tipo.startsWith('audio/') || /\.(mp3|ogg|wav|m4a|opus)$/.test(nome)) {
    return 'audio';
  }
  return 'arquivo';
}

export function ehImagem(anexo: APIAttachment): boolean {
  return tipoDe(anexo) === 'imagem';
}

/** Anexos; com a mensagem, clicar na imagem abre o visualizador. */
export function Anexos({
  anexos,
  mensagem,
}: {
  anexos: APIAttachment[];
  mensagem?: APIMessage;
}) {
  if (!anexos.length) return null;
  return (
    <div className="flex flex-col items-start gap-1.5 pt-1">
      {anexos.map((anexo) => (
        <Anexo
          key={anexo.id}
          anexo={anexo}
          aoAbrir={
            mensagem
              ? () =>
                  abrirCamada({ tipo: 'imagem', mensagem, anexoId: anexo.id })
              : undefined
          }
        />
      ))}
    </div>
  );
}

function Anexo({
  anexo,
  aoAbrir,
}: {
  anexo: APIAttachment;
  aoAbrir?: () => void;
}) {
  const spoiler = anexo.filename.startsWith('SPOILER_');
  const spoilerAberto = usePreferencias((prefs) => prefs.spoiler === 'sempre');
  const [revelado, setRevelado] = useState(!spoiler || spoilerAberto);
  // "Mostrar imagens e vídeos enviados" (U7) desligado: vira arquivo.
  const mostrarMidia = useAjusteDaConta('inline_attachment_media');
  const tipo = tipoDe(anexo);
  if ((tipo === 'imagem' || tipo === 'video') && mostrarMidia) {
    const tamanho = caber(anexo.width, anexo.height);
    return (
      <div
        className="relative overflow-hidden rounded-lg border border-borda bg-elevado"
        style={tamanho}
      >
        {tipo === 'imagem' ? (
          <a
            href={anexo.url}
            target="_blank"
            rel="noreferrer"
            aria-label={`Abrir ${anexo.filename}`}
            onClick={(evento) => {
              if (!aoAbrir || evento.ctrlKey || evento.metaKey) return;
              evento.preventDefault();
              aoAbrir();
            }}
          >
            <img
              src={noTamanho(
                anexo.proxy_url || anexo.url,
                Math.max(tamanho.width, tamanho.height),
              )}
              alt={anexo.description ?? anexo.filename}
              loading="lazy"
              width={tamanho.width}
              height={tamanho.height}
              className={`block h-full w-full object-cover ${revelado ? '' : 'blur-2xl'}`}
            />
          </a>
        ) : (
          <video
            src={anexo.url}
            controls
            preload="metadata"
            width={tamanho.width}
            height={tamanho.height}
            className={`block h-full w-full bg-black ${revelado ? '' : 'blur-2xl'}`}
          />
        )}
        {!revelado && (
          <button
            type="button"
            onClick={() => setRevelado(true)}
            className="absolute inset-0 flex items-center justify-center"
          >
            <span className="rounded-full bg-black/70 px-3 py-1 text-xs font-bold tracking-[.04em] text-white">
              SPOILER
            </span>
          </button>
        )}
      </div>
    );
  }
  if (tipo === 'audio') {
    return (
      <div className="flex w-[420px] max-w-full flex-col gap-2 rounded-lg border border-borda bg-elevado p-3">
        <NomeDoArquivo anexo={anexo} />
        <audio src={anexo.url} controls preload="none" className="w-full" />
      </div>
    );
  }
  return (
    <div className="flex w-[420px] max-w-full items-center gap-3 rounded-lg border border-borda bg-elevado p-3">
      <Icone nome="arquivo" tamanho={30} className="text-texto-3" />
      <NomeDoArquivo anexo={anexo} />
      <a
        href={anexo.url}
        download={anexo.filename}
        aria-label={`Baixar ${anexo.filename}`}
        className="flex size-8 shrink-0 items-center justify-center rounded-lg text-texto-2 hover:bg-ativo hover:text-texto"
      >
        <Icone nome="download" tamanho={20} />
      </a>
    </div>
  );
}

function NomeDoArquivo({ anexo }: { anexo: APIAttachment }) {
  return (
    <div className="min-w-0 grow">
      <a
        href={anexo.url}
        target="_blank"
        rel="noreferrer"
        className="block truncate text-[15px] font-medium text-link hover:underline"
      >
        {anexo.filename}
      </a>
      <span className="text-xs text-texto-3">{formatFileSize(anexo.size)}</span>
    </div>
  );
}

/**
 * Embeds como o Discord: o "rich" (bots, alertas) com a barra de cor, e os
 * de link, imagem e vídeo vindos das prévias do histórico.
 */
export function Embeds({
  embeds,
  guildId,
}: {
  embeds: APIEmbed[];
  guildId: string | null;
}) {
  if (!embeds.length) return null;
  return (
    <div className="flex flex-col items-start gap-1.5 pt-1">
      {embeds.map((embed, indice) => (
        <Embed key={indice} embed={embed} guildId={guildId} />
      ))}
    </div>
  );
}

function Embed({
  embed,
  guildId,
}: {
  embed: APIEmbed;
  guildId: string | null;
}) {
  const autoplay = useAjusteDaConta('gif_auto_play');
  // Imagem ou gif solto (link de imagem): só a mídia.
  if (
    (embed.type === 'image' || embed.type === 'gifv') &&
    (embed.thumbnail || embed.image || embed.video)
  ) {
    const midia = embed.video ?? embed.image ?? embed.thumbnail;
    if (!midia) return null;
    const tamanho = caber(midia.width, midia.height);
    return embed.type === 'gifv' && embed.video ? (
      <video
        src={embed.video.proxy_url ?? embed.video.url}
        poster={embed.thumbnail?.proxy_url ?? embed.thumbnail?.url}
        autoPlay={autoplay}
        // Sem reprodução automática (U7), o GIF anda com o mouse em cima.
        onMouseEnter={(evento) => !autoplay && void evento.currentTarget.play()}
        onMouseLeave={(evento) => !autoplay && evento.currentTarget.pause()}
        loop
        muted
        playsInline
        preload="metadata"
        className="rounded-lg bg-elevado object-cover"
        style={tamanho}
      />
    ) : (
      <a href={embed.url ?? midia.url} target="_blank" rel="noreferrer">
        <img
          src={noTamanho(
            midia.proxy_url ?? midia.url,
            Math.max(tamanho.width, tamanho.height),
          )}
          alt=""
          loading="lazy"
          className="rounded-lg object-cover"
          style={tamanho}
        />
      </a>
    );
  }
  const cor = embed.color ? roleColorHex(embed.color) : 'var(--cor-ativo)';
  const imagem = embed.image;
  const miniatura =
    embed.thumbnail && embed.type !== 'video' ? embed.thumbnail : null;
  const videoCapa =
    embed.type === 'video' ? (embed.thumbnail ?? embed.image) : null;
  const campos = embed.fields ?? [];
  return (
    <div className="flex max-w-[520px] overflow-hidden rounded-lg bg-elevado">
      <span
        aria-hidden="true"
        className="w-1 shrink-0"
        style={{ background: cor }}
      />
      <div className="flex min-w-0 grow gap-4 py-3 pr-4 pl-3">
        <div className="flex min-w-0 grow flex-col gap-2">
          {embed.provider?.name && (
            <span className="text-xs text-texto-3">{embed.provider.name}</span>
          )}
          {embed.author?.name && (
            <span className="flex items-center gap-2 text-sm font-semibold">
              {embed.author.icon_url && (
                <img
                  src={embed.author.proxy_icon_url ?? embed.author.icon_url}
                  alt=""
                  className="size-6 rounded-full"
                />
              )}
              {embed.author.url ? (
                <a
                  href={embed.author.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-texto hover:underline"
                >
                  {embed.author.name}
                </a>
              ) : (
                embed.author.name
              )}
            </span>
          )}
          {embed.title &&
            (embed.url ? (
              <a
                href={embed.url}
                target="_blank"
                rel="noreferrer"
                className="text-[15px] font-bold text-link hover:underline"
              >
                <Markdown texto={embed.title} guildId={guildId} inline />
              </a>
            ) : (
              <div className="text-[15px] font-bold">
                <Markdown texto={embed.title} guildId={guildId} inline />
              </div>
            ))}
          {embed.description && (
            <Markdown
              texto={embed.description}
              guildId={guildId}
              className="text-sm leading-normal text-texto-2"
            />
          )}
          {campos.length > 0 && (
            <div className="grid grid-cols-3 gap-x-4 gap-y-2.5">
              {campos.map((campo, indice) => (
                <div
                  key={indice}
                  className={campo.inline ? 'col-span-1' : 'col-span-3'}
                >
                  <div className="text-xs font-bold text-texto-2">
                    <Markdown texto={campo.name} guildId={guildId} inline />
                  </div>
                  <Markdown
                    texto={campo.value}
                    guildId={guildId}
                    className="text-sm"
                  />
                </div>
              ))}
            </div>
          )}
          {imagem && (
            <img
              src={noTamanho(imagem.proxy_url ?? imagem.url, 480)}
              alt=""
              loading="lazy"
              className="mt-1 rounded-md object-cover"
              style={caber(imagem.width, imagem.height, {
                largura: 480,
                altura: 300,
              })}
            />
          )}
          {videoCapa && (
            <a
              href={embed.url ?? videoCapa.url}
              target="_blank"
              rel="noreferrer"
              className="relative mt-1 block overflow-hidden rounded-md"
              style={caber(videoCapa.width, videoCapa.height, {
                largura: 480,
                altura: 270,
              })}
            >
              <img
                src={noTamanho(videoCapa.proxy_url ?? videoCapa.url, 480)}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover"
              />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="rounded-full bg-black/70 px-3 py-1 text-xs font-semibold text-white">
                  Abrir vídeo
                </span>
              </span>
            </a>
          )}
          {(embed.footer?.text || embed.timestamp) && (
            <span className="flex items-center gap-1.5 text-xs text-texto-3">
              {embed.footer?.icon_url && (
                <img
                  src={embed.footer.proxy_icon_url ?? embed.footer.icon_url}
                  alt=""
                  className="size-5 rounded-full"
                />
              )}
              {[
                embed.footer?.text,
                embed.timestamp ? formatMessageTime(embed.timestamp) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          )}
        </div>
        {miniatura && (
          <img
            src={noTamanho(miniatura.proxy_url ?? miniatura.url, 80)}
            alt=""
            loading="lazy"
            className="shrink-0 rounded-md object-cover"
            style={caber(miniatura.width, miniatura.height, {
              largura: 80,
              altura: 80,
            })}
          />
        )}
      </div>
    </div>
  );
}

export function Figurinhas({ itens }: { itens: APIStickerItem[] }) {
  if (!itens.length) return null;
  return (
    <div className="flex gap-2 pt-1">
      {itens.map((figurinha) =>
        figurinha.format_type === 3 ? (
          <span
            key={figurinha.id}
            className="flex size-40 items-center justify-center rounded-lg bg-elevado text-sm text-texto-3"
          >
            {figurinha.name}
          </span>
        ) : (
          <img
            key={figurinha.id}
            src={noTamanho(stickerUrl(ARQUIVOS, figurinha), 160)}
            alt={figurinha.name}
            title={figurinha.name}
            loading="lazy"
            className="size-40 object-contain"
          />
        ),
      )}
    </div>
  );
}

/** Mensagem encaminhada: o conteúdo da original, com a marca. */
export function Encaminhada({
  mensagem,
  guildId,
}: {
  mensagem: APIMessage;
  guildId: string | null;
}) {
  const navegar = useNavigate();
  const original = mensagem.message_snapshots?.[0]?.message;
  const referencia = mensagem.message_reference;
  // De onde veio (como o Discord: "#canal · dia", leva à original se dá para ver).
  const origem = useEstado((estado) => {
    const canal = referencia?.channel_id
      ? estado.channels[referencia.channel_id]
      : undefined;
    if (!canal) return null;
    if (canal.name) return `#${canal.name}`;
    return 'Mensagem direta';
  });
  if (!original) return null;
  const quando = original.timestamp ?? null;
  return (
    <div className="flex gap-3 pt-0.5">
      <span aria-hidden="true" className="w-1 shrink-0 rounded bg-ativo" />
      <div className="flex min-w-0 flex-col gap-1">
        <span className="flex items-center gap-1.5 text-[13px] text-texto-3 italic">
          <Icone nome="encaminhar" tamanho={14} />
          Encaminhada
        </span>
        {original.content && (
          <Markdown texto={original.content} guildId={guildId} />
        )}
        {original.attachments && <Anexos anexos={original.attachments} />}
        {original.embeds && (
          <Embeds embeds={original.embeds} guildId={guildId} />
        )}
        {(origem || quando) && (
          <button
            type="button"
            disabled={!origem || !referencia?.message_id}
            onClick={() =>
              navegar(
                `/canais/${referencia?.guild_id ?? '@me'}/${referencia?.channel_id}/${referencia?.message_id}`,
              )
            }
            className="w-fit text-left text-xs text-texto-3 enabled:hover:text-texto-2 enabled:hover:underline"
          >
            {[origem, quando ? diaCurtoComHora(quando) : null]
              .filter(Boolean)
              .join(' · ')}
          </button>
        )}
      </div>
    </div>
  );
}

const DIA_E_HORA = new Intl.DateTimeFormat('pt-BR', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

function diaCurtoComHora(quando: string) {
  return DIA_E_HORA.format(new Date(quando)).replace('.', '');
}
